import { StationAvatar } from '@app/components/base/stations/StationAvatar';
import { Box, Chip, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';
import {
  generateOptionId,
  getChipStyles,
  getGroupHeader,
  getOptionIcon,
} from '../StationFilterUtils';
import { OptionType, StationFilterOption } from '../types';

export interface StationFilterChipProps {
  option: StationFilterOption;
  size?: 'small' | 'medium' | 'large';
  onDelete?: () => void;
  readonly?: boolean; // If true, don't show delete button
}

export const StationFilterChip = ({
  option,
  size = 'medium',
  onDelete,
  readonly = false,
}: StationFilterChipProps) => {
  const { $t } = useIntl();
  const theme = useTheme();

  const sizeConfig = {
    small: {
      height: '28px',
      borderRadius: '14px',
      fontSize: '12px',
      iconSize: '14px',
      avatarSize: 20,
    },
    medium: {
      height: '42px',
      borderRadius: '21px',
      fontSize: '16px',
      iconSize: '16px',
      avatarSize: 24,
    },
    large: {
      height: '56px',
      borderRadius: '28px',
      fontSize: '18px',
      iconSize: '18px',
      avatarSize: 32,
    },
  };

  const config = sizeConfig[size];

  /** Main title variant scales with chip size (label row: title | type caption). */
  const labelTypography = size === 'small' ? 'caption' : size === 'large' ? 'body1' : 'body2';

  const showTypeCaption = option.type !== OptionType.IndividualStation;

  const avatar =
    option.type === OptionType.IndividualStation ? (
      <StationAvatar stationId={option.value.id} station={option.value} size={config.avatarSize} />
    ) : undefined;

  const iconOrNull =
    option.type !== OptionType.IndividualStation ? getOptionIcon(option.type) : undefined;

  return (
    <Chip
      key={generateOptionId(option)}
      avatar={avatar}
      icon={iconOrNull ?? undefined}
      label={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            minWidth: 0,
            width: '100%',
          }}
        >
          {showTypeCaption ? (
            <Box
              sx={{
                typography: size === 'large' ? 'body2' : 'caption',
                lineHeight: 1.2,
                color: 'text.secondary',
                flexShrink: 0,
                maxWidth: '38%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {getGroupHeader($t, option.type, 'short')}
            </Box>
          ) : null}
          <Box
            sx={{
              typography: labelTypography,
              lineHeight: 1.2,
              minWidth: 0,
              flex: '1 1 auto',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {option.label}
          </Box>
        </Box>
      }
      variant="outlined"
      onDelete={readonly ? undefined : onDelete}
      sx={{
        height: config.height,
        borderRadius: config.borderRadius,
        ...getChipStyles(option.type, theme),
        color: 'text.primary',
      }}
    />
  );
};
